import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AlertCircle, ArrowUpDown, ChevronLeft, Lock, Plus, Save, Search, Share2, SlidersHorizontal, Trash2, Users, X, Zap } from 'lucide-react';
import { useListStore } from '../store/listStore';
import { useAuthStore } from '../store/authStore';
import { listsApi } from '../lib/api';
import { ConfirmDialog } from '../components/common/ConfirmDialog';
import { ListItemCard, ListSortMenu, ShareListSheet, smartSortOptions, MANUAL_SORT_OPTIONS } from '../components/lists';
import { LoadMoreFooter } from '../components/lists/LoadMoreFooter';
import type { SortOption } from '../components/lists';
import type { FilterSpec, ListItemView } from '../types/list';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { canHandReorder, REORDER_CEILING } from '../lib/listReorder';

type TypeFilter = 'ALL' | 'SCHOOLS' | 'PROGRAMS' | 'PEOPLE';

/** Read-only chips summarizing a smart list's saved filter (Part 1 — raw values, no rich labels). */
function smartChips(spec: FilterSpec | null | undefined): string[] {
  if (!spec) return [];
  const chips: string[] = [];
  if (spec.search) chips.push(`"${spec.search}"`);
  if (spec.states?.length) chips.push(...spec.states);
  if (spec.ncesLevels?.length) chips.push(...spec.ncesLevels);
  if (spec.customerStatus?.length) chips.push(...spec.customerStatus);
  if (spec.stages?.length) chips.push(...spec.stages);
  if (spec.temperatures?.length) chips.push(...spec.temperatures);
  if (spec.sport) chips.push(spec.sport);
  if (spec.sports?.length) chips.push(...spec.sports);
  if (spec.levels?.length) chips.push(...spec.levels);
  if (spec.role) chips.push(spec.role);
  if (spec.hasBlastLink) chips.push('In Blast');
  if (spec.inBlast) chips.push('In Blast');
  if (spec.excludeDnc) chips.push('Exclude DNC');
  if (typeof spec.enrollmentMin === 'number') chips.push(`Enroll ≥ ${spec.enrollmentMin}`);
  if (typeof spec.enrollmentMax === 'number') chips.push(`Enroll ≤ ${spec.enrollmentMax}`);
  return chips;
}

function matchesFilter(item: ListItemView, filter: TypeFilter): boolean {
  switch (filter) {
    case 'SCHOOLS':
      return item.kind === 'SCHOOL' || item.kind === 'CLUB_TEAM';
    case 'PROGRAMS':
      return item.kind === 'PROGRAM';
    case 'PEOPLE':
      return item.kind === 'PERSON';
    default:
      return true;
  }
}

export default function ListDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const currentList = useListStore((s) => s.currentList);
  const items = useListStore((s) => s.items);
  const isLoadingDetail = useListStore((s) => s.isLoadingDetail);
  const detailError = useListStore((s) => s.detailError);
  const fetchList = useListStore((s) => s.fetchList);
  const fetchItems = useListStore((s) => s.fetchItems);
  const removeItem = useListStore((s) => s.removeItem);
  const deleteList = useListStore((s) => s.deleteList);
  const reorder = useListStore((s) => s.reorder);
  const setSort = useListStore((s) => s.setSort);
  const applySort = useListStore((s) => s.applySort);
  const itemsPagination = useListStore((s) => s.itemsPagination);
  const isLoadingMore = useListStore((s) => s.isLoadingMore);
  const fetchMoreItems = useListStore((s) => s.fetchMoreItems);
  const loadAllItems = useListStore((s) => s.loadAllItems);
  const itemsSearch = useListStore((s) => s.itemsSearch);
  const setItemsSearch = useListStore((s) => s.setItemsSearch);

  const user = useAuthStore((s) => s.user);
  const hasAnyRole = useAuthStore((s) => s.hasAnyRole);

  const [filter, setFilter] = useState<TypeFilter>('ALL');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [savingSnapshot, setSavingSnapshot] = useState(false);
  /** Non-null while reorder mode is active; holds the locally-reordered copy. */
  const [reorderItems, setReorderItems] = useState<ListItemView[] | null>(null);
  const [savingReorder, setSavingReorder] = useState(false);
  /** Manual-list sort pick awaiting confirmation (rank rewrite is destructive to manual order). */
  const [pendingManualSort, setPendingManualSort] = useState<SortOption | null>(null);
  const [sorting, setSorting] = useState(false);
  const [enteringReorder, setEnteringReorder] = useState(false);
  const [searchDraft, setSearchDraft] = useState('');

  const sentinelRef = useRef<HTMLDivElement>(null);

  // Keep the input in sync when the store resets (e.g. navigating between lists).
  useEffect(() => { setSearchDraft(itemsSearch); }, [itemsSearch]);
  // Debounced server-side search — the whole list, not just the loaded pages.
  useEffect(() => {
    if (!id) return;
    if (searchDraft === itemsSearch) return;
    const t = setTimeout(() => { void setItemsSearch(id, searchDraft.trim()); }, 300);
    return () => clearTimeout(t);
  }, [searchDraft, itemsSearch, id, setItemsSearch]);

  useEffect(() => {
    if (!id) return;
    fetchList(id);
    fetchItems(id);
  }, [id, fetchList, fetchItems]);

  const moreEnabled = reorderItems === null && filter === 'ALL' && !!itemsPagination?.hasNextPage && !isLoadingMore;
  useInfiniteScroll(sentinelRef, () => { if (id) void fetchMoreItems(id); }, moreEnabled);

  // Edit allowed for the owner, a manager/admin, or an EDIT grantee (mirrors backend canEdit in listAccess.ts).
  const canEdit = useMemo(() => {
    if (!currentList || !user) return false;
    if (currentList.ownerId === user.id || hasAnyRole('MANAGER', 'ADMIN')) return true;
    if (currentList.isSystem) return false;
    return (currentList.shares ?? []).some(
      (s) => s.permission === 'EDIT' && (s.granteeType === 'TEAM' || s.granteeUserId === user.id),
    );
  }, [currentList, user, hasAnyRole]);

  // Share management is owner-or-manager/admin only (mirrors backend requireOwnerOrAdmin) — an EDIT grantee can't re-share.
  const canManageShares = useMemo(() => {
    if (!currentList || !user || currentList.isSystem) return false;
    return currentList.ownerId === user.id || hasAnyRole('MANAGER', 'ADMIN');
  }, [currentList, user, hasAnyRole]);

  // Counts come from the server (true totals), not the loaded slice. kc is present for manual lists.
  const counts = useMemo(() => {
    const kc = currentList?.kindCounts;
    if (kc) {
      return { all: kc.SCHOOL + kc.CLUB_TEAM + kc.PERSON + kc.PROGRAM, schools: kc.SCHOOL + kc.CLUB_TEAM, programs: kc.PROGRAM, people: kc.PERSON };
    }
    // Fallback (smart lists / favorites — chips don't show there anyway)
    let schools = 0, programs = 0, people = 0;
    for (const it of items) {
      if (it.kind === 'SCHOOL' || it.kind === 'CLUB_TEAM') schools += 1;
      else if (it.kind === 'PROGRAM') programs += 1;
      else if (it.kind === 'PERSON') people += 1;
    }
    return { all: items.length, schools, programs, people };
  }, [currentList?.kindCounts, items]);

  const distinctKinds = (counts.schools > 0 ? 1 : 0) + (counts.programs > 0 ? 1 : 0) + (counts.people > 0 ? 1 : 0);
  const showChips = distinctKinds > 1;

  const visibleItems = useMemo(
    () => (filter === 'ALL' ? items : items.filter((it) => matchesFilter(it, filter))),
    [items, filter],
  );

  async function handleRemove(item: ListItemView) {
    if (!id || !item.id) return;
    try {
      await removeItem(id, item.id);
      toast.success('Removed from list');
    } catch {
      toast.error('Could not remove item');
    }
  }

  async function handleDelete() {
    if (!id) return;
    await deleteList(id);
    navigate('/lists');
  }

  async function handleSnapshot() {
    if (!id) return;
    setSavingSnapshot(true);
    try {
      const { list } = await listsApi.snapshot(id);
      toast.success('Saved as a manual list');
      navigate(`/lists/${list.id}`);
    } catch {
      toast.error('Could not save list');
    } finally {
      setSavingSnapshot(false);
    }
  }

  async function enterReorderMode() {
    if (!id) return;
    setEnteringReorder(true);
    try {
      // Ranks are global — reordering a searched subset would be misleading.
      if (itemsSearch !== '') { setSearchDraft(''); await setItemsSearch(id, ''); }
      if (items.length < (currentList?.count ?? items.length)) await loadAllItems(id);
      setReorderItems([...useListStore.getState().items]);
    } catch {
      toast.error('Could not load the full list to reorder');
    } finally {
      setEnteringReorder(false);
    }
  }

  // Selecting a type chip needs the whole list loaded so client-side filtering is complete.
  async function handleSelectFilter(key: TypeFilter) {
    if (key !== 'ALL' && id && items.length < (currentList?.count ?? items.length)) {
      try { await loadAllItems(id); } catch { toast.error('Could not load all items'); return; }
    }
    setFilter(key);
  }

  function exitReorderMode() {
    setReorderItems(null);
  }

  function moveItem(i: number, dir: -1 | 1) {
    setReorderItems((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }

  async function handleSaveReorder() {
    if (!id || !reorderItems) return;
    setSavingReorder(true);
    try {
      const orderedIds = reorderItems.map((it) => it.id).filter((itemId): itemId is string => Boolean(itemId));
      await reorder(id, orderedIds);
      toast.success('Order saved');
      setReorderItems(null);
    } catch {
      toast.error('Could not save order');
    } finally {
      setSavingReorder(false);
    }
  }

  async function handleSmartSort(opt: SortOption) {
    if (!id) return;
    setSorting(true);
    try {
      await setSort(id, opt.field, opt.dir);
      toast.success('Sort saved');
    } catch {
      toast.error('Could not save sort');
    } finally {
      setSorting(false);
    }
  }

  async function handleManualSortConfirmed() {
    if (!id || !pendingManualSort) return;
    setSorting(true);
    try {
      await applySort(id, pendingManualSort.field, pendingManualSort.dir);
      toast.success('List sorted — order saved');
    } catch {
      toast.error('Could not sort list');
    } finally {
      setSorting(false);
      setPendingManualSort(null);
    }
  }

  // Loading state (mirrors sibling detail pages).
  if (isLoadingDetail && !currentList) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blast-600 mx-auto"></div>
          <p className="mt-4 text-gray-500 dark:text-gray-400">Loading list...</p>
        </div>
      </div>
    );
  }

  // Error / not-found state.
  if (detailError || !currentList || !id) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
            {detailError ? 'Error loading list' : 'List not found'}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {detailError || "The list you're looking for doesn't exist or has been deleted."}
          </p>
          <Link to="/lists" className="btn-secondary">
            Back to Lists
          </Link>
        </div>
      </div>
    );
  }

  const isSmart = currentList.kind === 'SMART';
  const shareCount = currentList.shares?.length ?? 0;
  const itemCount = currentList.count ?? items.length;
  const chips = isSmart ? smartChips(currentList.filterSpec) : [];
  const reorderable = canHandReorder({ count: itemCount, isSmart, canEdit });
  const reorderTooLarge = !isSmart && canEdit && itemCount > REORDER_CEILING;
  const showReorderButton = reorderable && reorderItems === null;

  const chipDefs: { key: TypeFilter; label: string; count: number }[] = [
    { key: 'ALL', label: 'All', count: counts.all },
    { key: 'SCHOOLS', label: 'Schools', count: counts.schools },
    { key: 'PROGRAMS', label: 'Programs', count: counts.programs },
    { key: 'PEOPLE', label: 'People', count: counts.people },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back link */}
      <Link
        to="/lists"
        className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-2"
      >
        <ChevronLeft className="h-4 w-4" />
        Lists
      </Link>

      {/* Title + kind badge */}
      <div className="flex items-start justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 line-clamp-2 min-w-0">
          {currentList.name}
        </h1>
        {isSmart ? (
          <span className="flex-shrink-0 inline-flex items-center gap-1 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 mt-1.5">
            <Zap className="h-3 w-3" />
            SMART
          </span>
        ) : (
          <span className="flex-shrink-0 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300 mt-1.5">
            MANUAL
          </span>
        )}
      </div>

      {/* Meta row */}
      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mt-1.5">
        <span>
          {itemCount} {itemCount === 1 ? 'item' : 'items'}
        </span>
        <span>·</span>
        {shareCount > 0 ? (
          <span className="inline-flex items-center gap-1">
            <Users className="h-3.5 w-3.5" />
            {currentList.owner && user && currentList.ownerId !== user.id
              ? `Shared by ${currentList.owner.displayName ?? 'a teammate'}`
              : `Shared · ${shareCount}`}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1">
            <Lock className="h-3.5 w-3.5" />
            Private
          </span>
        )}
      </div>

      {/* Action row — hidden while in reorder mode to keep focus */}
      {reorderItems === null && (
        <div className="flex flex-wrap items-center gap-2 mt-4">
          {!isSmart && canEdit && (
            <button
              type="button"
              className="btn-primary"
              onClick={() => navigate('/explore', { state: { intent: 'add-to-manual', listId: id, listName: currentList.name } })}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add items
            </button>
          )}
          {isSmart && canEdit && currentList.smartEntityKind && currentList.filterSpec && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() =>
                navigate('/explore', {
                  state: {
                    intent: 'edit-smart',
                    listId: id,
                    entityKind: currentList.smartEntityKind,
                    initialFilterSpec: currentList.filterSpec,
                  },
                })
              }
            >
              <SlidersHorizontal className="h-4 w-4 mr-1.5" />
              Edit filter
            </button>
          )}
          {isSmart && (
            <button
              type="button"
              className="btn-secondary"
              onClick={handleSnapshot}
              disabled={savingSnapshot}
            >
              <Save className="h-4 w-4 mr-1.5" />
              {savingSnapshot ? 'Saving…' : 'Save as a manual list'}
            </button>
          )}
          {isSmart && canEdit && currentList.smartEntityKind && (
            <ListSortMenu
              options={smartSortOptions(currentList.smartEntityKind)}
              currentKey={currentList.sortField ? `${currentList.sortField}:${currentList.sortDir ?? 'asc'}` : null}
              onPick={(opt) => { void handleSmartSort(opt); }}
              disabled={sorting}
            />
          )}
          {!isSmart && canEdit && items.length >= 2 && (
            <ListSortMenu
              options={MANUAL_SORT_OPTIONS}
              currentKey={null}
              onPick={setPendingManualSort}
              disabled={sorting}
            />
          )}
          {showReorderButton && (
            <button type="button" className="btn-secondary" onClick={() => { void enterReorderMode(); }} disabled={enteringReorder}>
              <ArrowUpDown className="h-4 w-4 mr-1.5" />
              {enteringReorder ? 'Loading…' : 'Reorder'}
            </button>
          )}
          {reorderTooLarge && reorderItems === null && (
            <button
              type="button"
              className="btn-secondary opacity-50 cursor-not-allowed"
              disabled
              title={`Lists over ${REORDER_CEILING} items are too large to reorder by hand — use Sort to order them.`}
            >
              <ArrowUpDown className="h-4 w-4 mr-1.5" />
              Reorder
            </button>
          )}
          {canManageShares && (
            <button type="button" className="btn-secondary" onClick={() => setShareOpen(true)}>
              <Share2 className="h-4 w-4 mr-1.5" />
              Share
            </button>
          )}
          {canManageShares && (
            <button type="button" className="btn-danger" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4 mr-1.5" />
              Delete
            </button>
          )}
        </div>
      )}

      {/* Smart list: read-only filter chips */}
      {isSmart && chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {chips.map((chip, i) => (
            <span
              key={`${chip}-${i}`}
              className="bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full px-2.5 py-0.5 text-xs"
            >
              {chip}
            </span>
          ))}
        </div>
      )}

      {/* Find within the list — server-side, whole list (not just loaded pages) */}
      {reorderItems === null && (itemCount > 5 || itemsSearch !== '') && (
        <div className="relative mt-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
          <input
            type="search"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            placeholder="Search this list…"
            aria-label="Search within this list"
            className="input w-full pl-9 pr-9"
          />
          {searchDraft !== '' && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => setSearchDraft('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
      {reorderItems === null && itemsSearch !== '' && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          {itemsPagination?.totalCount ?? 0} {(itemsPagination?.totalCount ?? 0) === 1 ? 'match' : 'matches'}
        </p>
      )}

      {/* Type-filter chips — hidden in reorder mode (ranks are global; filtering a subset is confusing) */}
      {showChips && reorderItems === null && (
        <div
          role="radiogroup"
          aria-label="Filter items by type"
          className="flex gap-2 overflow-x-auto mt-5 -mx-1 px-1 pb-1"
        >
          {chipDefs.map(({ key, label, count }) => {
            const active = filter === key;
            return (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={active}
                onClick={() => { void handleSelectFilter(key); }}
                className={`flex-none rounded-full text-xs px-3 py-1.5 whitespace-nowrap transition-colors ${
                  active
                    ? 'bg-[#b7ee41] text-gray-900 font-semibold'
                    : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {label} · {count}
              </button>
            );
          })}
        </div>
      )}

      {/* Body */}
      <div className={`mt-5 ${reorderItems !== null ? 'pb-28' : ''}`}>
        {reorderItems !== null ? (
          /* ── Reorder mode: full list, chevron controls, no links ── */
          <div className="space-y-2">
            {reorderItems.map((item, i) => (
              <ListItemCard
                key={item.id ?? `${item.kind}-${item.organization?.id ?? item.person?.id ?? item.team?.id ?? i}`}
                item={item}
                index={i}
                canEdit={false}
                onRemove={handleRemove}
                reorderMode
                isFirst={i === 0}
                isLast={i === reorderItems.length - 1}
                onMoveUp={() => moveItem(i, -1)}
                onMoveDown={() => moveItem(i, 1)}
              />
            ))}
          </div>
        ) : visibleItems.length === 0 ? (
          <div className="text-center py-14 px-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 grid place-items-center mb-4">
              <Users className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-900 dark:text-gray-100 mb-1">
              {itemsSearch !== '' ? 'No matches in this list' : itemCount === 0 ? 'This list is empty' : 'Nothing matches this filter'}
            </h3>
            {itemsSearch !== '' ? (
              <button
                type="button"
                onClick={() => setSearchDraft('')}
                className="text-sm text-blast-600 hover:underline dark:text-blast-400"
              >
                Clear search
              </button>
            ) : itemCount === 0 ? (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
                  Add schools, programs, or people from their profile using{' '}
                  <span className="font-medium text-gray-700 dark:text-gray-300">Add to list</span>.{' '}
                  <Link to="/organizations" className="text-blast-600 hover:underline dark:text-blast-400">
                    Browse organizations
                  </Link>{' '}
                  or{' '}
                  <Link to="/people" className="text-blast-600 hover:underline dark:text-blast-400">
                    people
                  </Link>
                  .
                </p>
                {!isSmart && canEdit && (
                  <button
                    type="button"
                    className="btn-primary mt-4"
                    onClick={() =>
                      navigate('/explore', { state: { intent: 'add-to-manual', listId: id, listName: currentList.name } })
                    }
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    Add items
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setFilter('ALL')}
                className="text-sm text-blast-600 hover:underline dark:text-blast-400"
              >
                Show all items
              </button>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {visibleItems.map((item, i) => (
              <ListItemCard
                key={item.id ?? `${item.kind}-${item.organization?.id ?? item.person?.id ?? item.team?.id ?? i}`}
                item={item}
                index={i}
                canEdit={canEdit && !isSmart && !!item.id}
                onRemove={handleRemove}
              />
            ))}
          </div>
        )}
      </div>

      {reorderItems === null && filter === 'ALL' && itemCount > 0 && (
        <LoadMoreFooter
          loaded={items.length}
          total={itemsSearch !== '' ? (itemsPagination?.totalCount ?? 0) : itemCount}
          hasMore={!!itemsPagination?.hasNextPage}
          isLoading={isLoadingMore}
          onLoadMore={() => { if (id) void fetchMoreItems(id); }}
          sentinelRef={sentinelRef}
        />
      )}

      {/* Sticky reorder footer — mirrors BulkActionBar: fixed bottom-6, centered, dark surface, white text */}
      {reorderItems !== null && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-gray-900 dark:bg-gray-700 text-white rounded-lg shadow-xl px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4 max-w-[calc(100vw-2rem)]">
          <span className="text-sm font-medium whitespace-nowrap">Reorder items</span>
          <button
            type="button"
            onClick={() => { void handleSaveReorder(); }}
            disabled={savingReorder}
            className="btn-primary disabled:opacity-50 whitespace-nowrap"
          >
            {savingReorder ? 'Saving…' : 'Save order'}
          </button>
          <button
            type="button"
            onClick={exitReorderMode}
            disabled={savingReorder}
            className="btn-secondary disabled:opacity-50 whitespace-nowrap"
          >
            Cancel
          </button>
        </div>
      )}

      {canManageShares && (
        <ShareListSheet
          open={shareOpen}
          list={currentList}
          onClose={() => setShareOpen(false)}
          onChanged={() => { if (id) void fetchList(id); }}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete list?"
        message={`"${currentList.name}" will be permanently removed. This can't be undone.`}
        confirmLabel="Delete"
        danger
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
      <ConfirmDialog
        open={pendingManualSort !== null}
        title="Sort and save this order?"
        message={`Items will be reordered by ${pendingManualSort?.label.toLowerCase() ?? ''}. This replaces your current manual order (you can still fine-tune with Reorder afterward).`}
        confirmLabel="Sort & save"
        onConfirm={handleManualSortConfirmed}
        onCancel={() => setPendingManualSort(null)}
      />
    </div>
  );
}
